import { useEffect, useState } from 'react';
import { ACTIVITY_KEY, DEV_BYPASS, signOutForInactivity, stampActivity } from '../lib/auth';

/**
 * Signs the person out after 60 minutes without activity.
 *
 * Activity is any mouse, keyboard, scroll or touch input. It is stamped in
 * localStorage, so working in one tab keeps every open tab alive, and a
 * laptop that slept past the limit signs out as soon as it wakes. Two minutes
 * before the limit a warning offers to stay signed in.
 *
 * The sign-out is the app's own (signOutForInactivity): the MSP-OPS session
 * is cleared and the sign-in screen says why, without signing the person out
 * of Microsoft 365 everywhere else in the browser.
 */
export const IDLE_LIMIT_MS = 60 * 60_000;
const WARN_BEFORE_MS = 2 * 60_000;
const now = () => Date.now();
/** The shared last-activity stamp, or null when there is none (signed out, first visit). */
function stored(): number | null {
  try {
    const v = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
const lastActivity = () => stored() ?? now();
const stamp = stampActivity;

export function IdleSignOut() {
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (DEV_BYPASS) return;
    /* A session older than the limit on arrival — the tab was left, or the
       laptop slept — ends at once. Only a real stamp counts: none at all
       (just signed in, first visit) is a fresh start. */
    const was = stored();
    if (was !== null && now() - was >= IDLE_LIMIT_MS) {
      signOutForInactivity();
      return;
    }
    stamp();
    let lastStamp = now();
    const onActivity = () => {
      /* At most every few seconds: mousemove fires constantly. */
      if (now() - lastStamp > 5_000) {
        lastStamp = now();
        stamp();
      }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, onActivity, { passive: true });

    const tick = () => {
      const idle = now() - lastActivity();
      if (idle >= IDLE_LIMIT_MS) {
        signOutForInactivity();
        return;
      }
      const remaining = IDLE_LIMIT_MS - idle;
      setLeft(remaining <= WARN_BEFORE_MS ? remaining : null);
    };
    const timer = window.setInterval(tick, 1_000);
    /* A tab coming back into view checks straight away rather than on the next tick. */
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      for (const e of events) window.removeEventListener(e, onActivity);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (left === null) return null;
  const secs = Math.max(0, Math.ceil(left / 1000));
  return (
    <div className="idle-warning" role="alertdialog" aria-live="assertive" aria-label="You are about to be signed out">
      <div className="idle-card">
        <strong>Still there?</strong>
        <p className="small">
          You&apos;ll be signed out in{' '}
          <span className="mono">
            {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
          </span>{' '}
          because there has been no activity for a while.
        </p>
        <div className="row" style={{ gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => signOutForInactivity('manual')}>
            Sign out now
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            autoFocus
            onClick={() => {
              stamp();
              setLeft(null);
            }}
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
